import { SidePanelContent, SidePanelHeader } from "$root/components/common/SidePanelV2";
import { StackSidePanelContext } from "$root/components/common/Stack/StackSidePanel";
import { Divider } from "$root/components/Divider";
import { Badge, ClickableDiv, Column, Icon, type IconName, Row, Text } from "@mdotm/shadcn/legacy/components";
import { readAtom, useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { Activity, type MouseEvent } from "react";
import { WorkspaceChartSettingsStack } from "./workspaceChart/WorkspaceChartSettings";
import { WorkspaceTableSettingStack } from "./workspaceTable/WorkspaceTableSettings";
import { useWorkspaceColumns } from "./hooks";
import { createSortLabel } from "./common";
import { useWorkspaceSlices } from "./state";
import { pickDataAttrs, type DataAttributesProps } from "@mdotm/shadcn/mdotm/components";
import { qualifier } from "$root/utils";

type WorkspaceSettingItemProps = {
	icon: IconName;
	title: string;
	description: string;
	count?: number;
	onClick?(): void;
} & DataAttributesProps;

function avoidPropagation(fn: () => void) {
	return function eventHandler<T extends MouseEvent>(e: T) {
		if (e.isPropagationStopped()) {
			return;
		}
		e.stopPropagation();
		fn();
	};
}

function WorkspaceSettingsItem(props: WorkspaceSettingItemProps) {
	return (
		<ClickableDiv
			onClick={avoidPropagation(() => props.onClick?.())}
			className={{
				"cursor-pointer": props.onClick !== undefined,
				"flex-1": true,
			}}
			{...pickDataAttrs(props)}
		>
			<Row
				alignItems="center"
				columnGap={16}
				className={`${props.onClick && "hover:bg-neutral-100"} py-2 px-2 rounded-sm min-h-12`}
			>
				<div className="rounded bg-(--palette-N50) w-8 h-8 flex items-center justify-center">
					<Icon icon={props.icon} size={16} color={themeCSSVars.palette_N600} />
				</div>
				<Column>
					<Text type="Body/L/Bold">{props.title}</Text>
					<Text type="Body/M/Book">{props.description}</Text>
				</Column>
				<Row justifyContent="end" alignItems="center">
					<Activity mode={props.count ? "visible" : "hidden"}>
						<Badge
							className="w-4 h-4 justify-center"
							backgroundColor={themeCSSVars.palette_N400}
							color={themeCSSVars.palette_N0}
						>
							<Text type="Body/M/Bold" color={themeCSSVars.palette_N0}>
								{props.count}
							</Text>
						</Badge>
					</Activity>
					{props.onClick && <Icon icon="Right" size={16} color={themeCSSVars.palette_N500} />}
				</Row>
			</Row>
		</ClickableDiv>
	);
}

export function WorkspaceSettingsStack() {
	const sidePanelCtx = useMandatoryContext(StackSidePanelContext);
	const slices = useWorkspaceSlices();
	const nOfCharts = readAtom(slices.charts$).length;
	const nOfColumns = useWorkspaceColumns().watchWorkspaceColumns()?.length ?? 0;
	const allParametrizedConcepts = useAtomValue(slices.concepts$);
	const sort = useAtomValue(slices.sort$);

	return (
		<>
			<SidePanelHeader title="Display settings" description="Configure display options" icon={"Settings"} />
			<Divider />
			<SidePanelContent>
				<Text type="Body/L/Bold" className="mb-6" as="p">
					Settings elements
				</Text>
				<Column gap={8}>
					<WorkspaceSettingsItem
						title="Table Settings"
						description="Manage columns and table display options"
						count={nOfColumns}
						icon="Table"
						onClick={() => sidePanelCtx.push(WorkspaceTableSettingStack)}
						data-qualifier={qualifier.component.sidePanelClickableDiv("TableSettings")}
					/>
					<WorkspaceSettingsItem
						title="Chart Settings"
						description="Configure chart widgets and visualizations"
						count={nOfCharts}
						icon="Chart"
						onClick={() => sidePanelCtx?.push(WorkspaceChartSettingsStack)}
						data-qualifier={qualifier.component.sidePanelClickableDiv("ChartSettings")}
					/>
					<WorkspaceSettingsItem
						title="Sorted by"
						description={createSortLabel(sort, allParametrizedConcepts)}
						icon="sorting"
						data-qualifier={qualifier.component.sidePanelDiv("Sort")}
					/>
				</Column>
			</SidePanelContent>
		</>
	);
}
